import React, { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { mixpanelTrack } from '@providers/Mixpanel';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import ProgressIndicator from '@components/ProgressIndicator';
import Button from '@/components/DesignLibrary/Button';
import WarningCircle from '@/components/Icons/WarningCircle';
import XCircle from '@components/Icons/XCircle';
import Bluebird from 'bluebird';
import formatAmountWithCurrency from '@format/priceWithCurrency';
import { Currency } from '@/typings/api-models';
import { useOpenDocumentRequestUserFilePane } from '@/panes/documentRequestUserFileHooks';

import {
  Document,
  DocumentRequestFile,
  DocumentRequestFileStatus,
  DocumentRequestStatus,
  DocumentRequestUserFile,
  PaystubAnalysis } from '@/tsClient/servers/noodleDocuments/document-api-models';
import DocumentFileCard from '@components/Document/DocumentFileCard';
import FileArrowDown from '@components/Icons/FileArrowDown';
import Plus from '@components/Icons/Plus';
import Workflow from '@components/Icons/Workflow';
import Spacer from '@components/Spacer';
import { DocumentAccessContext, DocumentAccessGate } from '@providers/DocumentAccess';
import { useIsMobile, useToast, useUser } from '@hooks';
import CustomLink from '@components/CustomLink';
import { IDENTIFIERS, getUrl, getUrlBase } from '@helpers/urlsHelper';
import preloadDocument from '@helpers/preloadDocument';
import { logError } from '@providers/ErrorTracking';
import teamsContext from '@providers/Teams/TeamsContext';
import Buttons from '@components/Buttons/Buttons';
import Modal from '@components/Modal';
import UserImage from '@components/UserImage';
import { getUserWorkflowById, getUserWorkflowCollaborators } from '@tsClient/workflows';
import AddDocumentModal from '@modals/AddDocumentModal';
import Head from 'next/head';
import Header from '@components/DesignLibrary/Header';
import userWorkflowToContext from '@helpers/userWorkflowToContext';
import PaperPlaneRight from '@components/Icons/PaperPlaneRight';
import Check from '@components/Icons/Check';
import Reopen from '@components/Icons/Reopen';
import Tabs from '@components/DesignLibrary/Tabs';
import { SIZES } from '@styles/media';
import BannerStatus from '@components/DesignLibrary/BannerStatus';
import MainPanelSubLayout from '@layouts/MainPanelSubLayout';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import UserWorkflowDiscussion from '@lib/Creators/Dashboard/Workflows/UserWorkflowWidgets/UserWorkflowDiscussion';
import { useRouter } from 'next/router';
import * as format from '@format';
import { useEventsContext } from '@/providers/Events';
import AddIncomeSourceModal from '@/modals/AddIncomeSourceModal';
import ToggleLeft from '@/components/Icons/ToggleLeft';
import ToggleRight from '@/components/Icons/ToggleRight';
import { TBody, TD, TH, THead, TFoot, Table, TableRow } from '@/components/DesignLibrary/Table';
import { Checkbox, Input } from '@/components/DesignLibrary/Atoms';
import { useFeatureFlag } from '@/providers/FeatureFlags';
import { IconWeight } from '@phosphor-icons/react';
import pluralize from 'pluralize';
import CaretUp from '@components/Icons/CaretUp';
import CaretDown from '@components/Icons/CaretDown';
import Label from '@/components/DesignLibrary/Atoms/shared/Label';
import { useQueryState } from 'nuqs';
import { DOC_ID_QS_KEY } from '@/lib/DocumentRequestUserFileDetail/DocumentRequestUserFileDetail';

import { WorkflowCollaborator } from '@/hooks/useUserWorkflowContext';
import FilePdf from '@/components/Icons/FilePdf';
import { getConsolidatedData } from '@/tsClient/documentRequests';
import NoodleAI from '@/components/Icons/NoodleAI';
import Tooltip from '@/components/Tooltip';
import EditNonEmploymentIncomeSourceModal from '@/modals/EditNonEmploymentIncomeSourceModal';
import useSocketContext from '@/providers/Socket/useSocketContext';
import { SocketMessageType } from '@/providers/Socket';
import { format as formatDateFns} from 'date-fns';
import PencilSimple from '@components/Icons/PencilSimple';
import s from './DocumentRequestUserDetail.module.scss';
import PayOrganizerFilePreview from './PayOrganizerFilePreview';
import MeansTestSection from './MeansTestSection';
import { FlatConsolidatedData } from './Types';

type ThisDocumentRequestUserFile = Pick<
  DocumentRequestUserFile,
  'id' | 'status' | 'isRequired' | 'hasPermission' | 'order' | 'fileName' | 'fileDescription' | 'isForYourEyesOnly' | 'rejectedAt' | 'rejectionMessage'
> & {
  documentRequestFile: Pick<DocumentRequestFile, 'fileName' | 'fileDescription' | 'order' | 'isForYourEyesOnly'> | null;
  documents: Array<Pick<Document, 'id' | 'fileName' | 'createdAt' | 'uploadedBy' | 'mimeType' | 'uploadedAt' | 'rank'>>;
};
type Props = {
  documentRequestUserId: string;
  creatorSlug: string;
};

type Tabs = 'Overview' | 'Discussion';

const tableColumns = [
  "Include in Monthly Totals",
  "Include in Means Test",
  "Employer",
  "Pay Cadence",
  "Pay Date",
  "Amount per Pay Period",
  "Overtime per Pay Period",
  "Tax, Medicare, and Social Security deductions",
  "Mandatory contributions for retirement plans",
  "Voluntary contributions for retirement plans",
  "Required repayments of retirement fund loans",
  "Insurance",
  "Domestic support obligations",
  "Union dues",
  "Other deductions",
  "Net Pay",
];

const keyToLabel: Record<string, string> = {
  amountPerPayPeriod: "Amount per Pay Period",
  domesticSupportObligations: "Domestic support obligations",
  insurance: "Insurance",
  mandatoryContributionsForRetirementPlans: "Mandatory contributions for retirement plans",
  netPay: "Net Pay",
  otherDeductions: "Other deductions",
  overtimePerPayPeriod: "Overtime per Pay Period",
  payDate: "Pay Date",
  requiredRepaymentsOfRetirementFundLoans: "Required repayments of retirement fund loans",
  taxMedicareAndSocialSecurityDeductions: "Tax, Medicare, and Social Security deductions",
  unionDues: "Union dues",
  voluntaryContributionsForRetirementPlans: "Voluntary contributions for retirement plans",
};

const DocumentRequestUserDetail: FC<Props> = ({ documentRequestUserId, creatorSlug }) => {
  const [isTableViewOpen, setIsTableViewOpen] = useState(false);
  const [documentRequestUserFiles, setDocumentRequestUserFiles] = useState<ThisDocumentRequestUserFile[]>([]);
  const [documentRequest, setDocumentRequest] = useState<Awaited<ReturnType<typeof tsClient.documentRequests.getDocumentRequestUser>> | null>(null);
  const userWorkflowId = documentRequest?.userWorkflowId;
  const { accessToken } = useContext(DocumentAccessContext);
  const { creatorId } = useContext(teamsContext);
  const [compiledBlobUrl, setCompiledBlobUrl] = useState<string | null>(null);
  const [isUpdatingDocumentRequest, setIsUpdatingDocumentRequest] = useState(false);
  const [collaborators, setCollaborators] = useState<WorkflowCollaborator[]>();
  const [isConfirmSubmitModalOpen, setIsConfirmSubmitModalOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState<'pending' | 'uploaded'>('pending');
  const [isAddDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
  const [containerOffset, setContainerOffset] = useState(180);
  const [valueModalOpen, setValueModalOpen] = useState(false);
  const [paystubModalOpen, setPaystubModalOpen] = useState<string | null>(null);
  const [payDateSortDirection, setPayDateSortDirection] = useState<'asc' | 'desc'>("asc");
  const [editNonEmploymentIncomeModalOpen, setEditNonEmploymentIncomeModalOpen] = useState<string|null>(null);
  const canShowTable = useFeatureFlag('can-show-pay-organizer-table');
  const [_, setDocumentId] = useQueryState(DOC_ID_QS_KEY);
  const [filePreview, setFilePreview] = useState<{
    documentRequestUserFileId: string;
    documentId: string;
  } | null>(null);
  const [user] = useUser();
  const userId = user?.id;
  const isMobile = useIsMobile();
  const isTabsLayout = useIsMobile(SIZES.lg);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isInDashboard } = useContext(DashboardContext);
  const router = useRouter();
  const [consolidatedData, setConsolidatedData] = useState<{
    rows: (FlatConsolidatedData & {
      rows: FlatConsolidatedData[];
    })[];
    total: FlatConsolidatedData;
      } | null>(null);
  const [collapsedRows, setCollapsedRows] = useState<string[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [selectedForMeanTest, setSelectedForMeanTest] = useState<string[]>([]);

  useEffect(() => {
    mixpanelTrack('DocumentRequest - viewed', { creatorSlug, documentRequestUserId });
  }, [creatorSlug, documentRequestUserId]);

  const { data: documentRequestUser, getData: getDocumentRequestUserFn } = useNoodleApi(tsClient.documentRequests.getDocumentRequestUser);
  const { getData: updateInternalDataFn } = useNoodleApi(tsClient.documentRequests.updateInternalDataForDocument);
  const openDocumentRequestUserFilePane = useOpenDocumentRequestUserFilePane();

  const {
    data: documentRequestUserFilesLoaded,
    getData: getDocumentRequestUserFilesFn,
    fetchingState: { isFetching: isFetchingDocumentRequestUserFiles },
  } = useNoodleApi(tsClient.documentRequests.getDocumentRequestUserFiles);

  const { getData: getDocumentRequestUserFile } = useNoodleApi(tsClient.documentRequests.getDocumentRequestUserFile);

  const { data: creator, getData: getCreatorFn } = useNoodleApi(tsClient.getCreator);

  const { getData: completeDocumentRequestFn } = useNoodleApi(tsClient.documentRequests.completeDocumentRequest, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Document request completed successfully'],
  });
  const { getData: reOpenDocumentRequestFn } = useNoodleApi(tsClient.documentRequests.reOpenDocumentRequest, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Document request reopened successfully'],
  });
  const { getData: updateDocumentRequestUser } = useNoodleApi(tsClient.documentRequests.updateDocumentRequestUser, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Document request updated successfully'],
  });
  const { getData: updateDocumentRequestUserModal } = useNoodleApi(tsClient.documentRequests.updateDocumentRequestUserForModalState, {
    toastOnError: true,
  });
  const {
    data: workflowData,
    fetchingState: fetchingWorkflow,
    getData: getWorkflow,
  } = useNoodleApi(getUserWorkflowById, {
    toastOnError: true,
  });

  const { data: loadedCollaborators, getData: getCollaboratorsFn } = useNoodleApi(getUserWorkflowCollaborators);

  const { data: fetchedData, getData:getConsolidatedDataFn} = useNoodleApi(getConsolidatedData);

  const { getData: updateDocumentRequestUserFileFn } = useNoodleApi(tsClient.documentRequests.updateDocumentRequestUserFile);
  const {
    data: meansTestData,
    getData: getMeansTestDataFn,
    fetchingState: {isFetching: fetchingMeansTestData},
  } = useNoodleApi(tsClient.ai.getMeansTestData);

  const fetchConsolidatedData = useCallback(async (): Promise<void> => {
    const res = await getConsolidatedDataFn({
      documentRequestUserId,
    });
    if (res.data) {

      setConsolidatedData({
        rows: res.data.files.filter(file => file.data).map((file) => ({
          "Amount per Pay Period": (file.data as PaystubAnalysis).amountPerPayPeriod,
          "Domestic support obligations": (file.data as PaystubAnalysis).domesticSupportObligations,
          "Employer": `${file.fileName} ${file.metadata?.incomeSource && file.metadata?.incomeSource!=='Employment'? '' : `(${file.documents.length} ${pluralize('paystub', file.documents.length)})`}`,
          "Insurance": (file.data as PaystubAnalysis).insurance,
          "Mandatory contributions for retirement plans": (file.data as PaystubAnalysis).mandatoryContributionsForRetirementPlans,
          "Net Pay": (file.data as PaystubAnalysis).netPay,
          "Other deductions": (file.data as PaystubAnalysis).otherDeductions,
          "Overtime per Pay Period": (file.data as PaystubAnalysis).overtimePerPayPeriod,
          "Pay Cadence": file.metadata?.payFrequency ?? '-',
          "Pay Date": null,
          "Required repayments of retirement fund loans": (file.data as PaystubAnalysis).requiredRepaymentsOfRetirementFundLoans,
          "Tax, Medicare, and Social Security deductions": (file.data as PaystubAnalysis).taxMedicareAndSocialSecurityDeductions,
          "Total Gross Income": (file.data as PaystubAnalysis).grossPay,
          "Union dues": (file.data as PaystubAnalysis).unionDues,
          "Voluntary contributions for retirement plans": (file.data as PaystubAnalysis).voluntaryContributionsForRetirementPlans,
          id: file.id,
          incomeSource: file.metadata?.incomeSource?? null,
          rows: [...file.documents.filter(d => d.data?.tableData).map((document) => ({
            "Amount per Pay Period": (document.data.tableData as PaystubAnalysis).amountPerPayPeriod,
            "Domestic support obligations": (document.data.tableData as PaystubAnalysis).domesticSupportObligations,
            "Employer": document.fileName,
            "Insurance": (document.data.tableData as PaystubAnalysis).insurance,
            "Mandatory contributions for retirement plans": (document.data.tableData as PaystubAnalysis).mandatoryContributionsForRetirementPlans,
            "Net Pay": (document.data.tableData as PaystubAnalysis).netPay,
            "Other deductions": (document.data.tableData as PaystubAnalysis).otherDeductions,
            "Overtime per Pay Period": (document.data.tableData as PaystubAnalysis).overtimePerPayPeriod,
            "Pay Cadence": file.metadata?.payFrequency ?? '-',
            "Pay Date": (document.data.tableData as PaystubAnalysis).payDate,
            "Required repayments of retirement fund loans": (document.data.tableData as PaystubAnalysis).requiredRepaymentsOfRetirementFundLoans,
            "Tax, Medicare, and Social Security deductions": (document.data.tableData as PaystubAnalysis).taxMedicareAndSocialSecurityDeductions,
            "Total Gross Income": (document.data.tableData as PaystubAnalysis).grossPay,
            "Union dues": (document.data.tableData as PaystubAnalysis).unionDues,
            "Voluntary contributions for retirement plans": (document.data.tableData as PaystubAnalysis).voluntaryContributionsForRetirementPlans,
            id: document.id,
          })),
          ...(Array.isArray(file.metadata?.monthlyDollarValue)
            ? file.metadata.monthlyDollarValue.map(value => ({
              "Amount per Pay Period": value.income.amount,
              "Domestic support obligations": null,
              "Employer": formatDateFns(new Date(value.month), 'MMM yyyy'),
              "Insurance": null,
              "Mandatory contributions for retirement plans": null,
              "Net Pay": null,
              "Other deductions": null,
              "Overtime per Pay Period": null,
              "Pay Cadence": file.metadata?.payFrequency ?? '-',
              "Pay Date": value.month,
              "Required repayments of retirement fund loans": null,
              "Tax, Medicare, and Social Security deductions": null,
              "Total Gross Income": value.income.amount,
              "Union dues": null,
              "Voluntary contributions for retirement plans": null,
              id: `${file.id}-${value.month}`,
            }))
            : []
          ),

          ],
        })),
        total: {
          "Amount per Pay Period": (((res.data.total as PaystubAnalysis).amountPerPayPeriod) ?? 0),
          "Domestic support obligations": (res.data.total as PaystubAnalysis).domesticSupportObligations,
          "Employer": null,
          "Include in Monthly Totals":'Monthly Totals for Schedule I',
          "Insurance": (res.data.total as PaystubAnalysis).insurance,
          "Mandatory contributions for retirement plans": (res.data.total as PaystubAnalysis).mandatoryContributionsForRetirementPlans,
          "Net Pay": (res.data.total as PaystubAnalysis).netPay,
          "Other deductions": (res.data.total as PaystubAnalysis).otherDeductions,
          "Overtime per Pay Period": (res.data.total as PaystubAnalysis).overtimePerPayPeriod,
          "Pay Cadence": null,
          "Pay Date": null,
          "Required repayments of retirement fund loans": (res.data.total as PaystubAnalysis).requiredRepaymentsOfRetirementFundLoans,
          "Tax, Medicare, and Social Security deductions": (res.data.total as PaystubAnalysis).taxMedicareAndSocialSecurityDeductions,
          "Total Gross Income": (res.data.total as PaystubAnalysis).grossPay,
          "Union dues": (res.data.total as PaystubAnalysis).unionDues,
          "Voluntary contributions for retirement plans": (res.data.total as PaystubAnalysis).voluntaryContributionsForRetirementPlans,
          id: 'total',
        },
      });

      const useInCalculationIds = res.data.files.flatMap(item => {
        const calculationIds: string[] = [];
        const documentIds = item.documents
          .filter(doc => doc.data?.useInCalculation === true)
          .map(doc => doc.id);

        if (documentIds.length > 0) {
          calculationIds.push(...documentIds);
        }

        if (Array.isArray(item.metadata?.monthlyDollarValue)) {
          item.metadata.monthlyDollarValue.forEach(mdv => {
            if (mdv.useInCalculation) calculationIds.push(`${item.id}-${mdv.month}`);
          });
        }

        return calculationIds;
      });
      setSelectedDocuments(useInCalculationIds);

      const useInMeansTestIds = res.data.files.flatMap(item => {
        const meansTestIds: string[] = [];
        const documentIds = item.documents
          .filter(doc => doc.data?.useInMeansTest === true)
          .map(doc => doc.id);

        if (documentIds.length > 0) {
          meansTestIds.push(...documentIds);
        }

        if (Array.isArray(item.metadata?.monthlyDollarValue)) {
          item.metadata.monthlyDollarValue.forEach(mdv => {
            if (mdv.useInMeansTest) meansTestIds.push(`${item.id}-${mdv.month}`);
          });
        }

        return meansTestIds;
      });
      setSelectedForMeanTest(useInMeansTestIds);
    }
  }, [documentRequestUserId, getConsolidatedDataFn]);

  const {
    addListener: addSocketListener,
    removeListener: removeSocketListener,
    isInitialized: isSocketInitialized,
    joinChannels,
  } = useSocketContext();

  useEffect(() => {
    if (isSocketInitialized && documentRequestUserId) {
      joinChannels([`ai-means-test-data/${documentRequestUserId}`]);
    }
  }, [joinChannels, documentRequestUserId, isSocketInitialized]);

  useEffect(() => {
    const listenerId = addSocketListener({
      fn: () => {
        if (userWorkflowId && documentRequestUserId && isTableViewOpen) {
          getMeansTestDataFn({ documentRequestUserId, userWorkflowId });
        }
      },
      messageType: SocketMessageType.MEANS_TEST_AI_REQUEST_STATUS_UPDATED,
    });

    return () => {
      removeSocketListener(listenerId);
    };
  }, [addSocketListener, removeSocketListener, getMeansTestDataFn, documentRequest?.id, userWorkflowId, documentRequestUserId, isTableViewOpen]);

  useEffect(() => {
    if (documentRequestUserId && isTableViewOpen) {
      fetchConsolidatedData();
    }
  }, [documentRequestUserId, fetchConsolidatedData, isTableViewOpen]);

  useEffect(() => {
    if (userWorkflowId && documentRequestUserId && isTableViewOpen) {
      getMeansTestDataFn({documentRequestUserId, userWorkflowId });
    }
  }, [documentRequest?.id, userWorkflowId, documentRequestUserId, getMeansTestDataFn, isTableViewOpen]);

  const fetchCollaborators = useCallback(() => {
    if (workflowData?.id) {
      getCollaboratorsFn({ userWorkflowId: workflowData?.id });
    }
  },[workflowData, getCollaboratorsFn]);

  useEffect(() => {
    fetchCollaborators();
  }, [fetchCollaborators]);

  useEffect(() => {
    if (loadedCollaborators) {
      setCollaborators(loadedCollaborators);
    }
  }, [loadedCollaborators, setCollaborators]);

  const refetchFiles = useCallback(async () => {
    try {
      setIsUpdatingDocumentRequest(true);
      const [userResponse, filesResponse] = await Promise.all([
        getDocumentRequestUserFn({ id: documentRequestUserId }),
        getDocumentRequestUserFilesFn({
          accessToken: accessToken || undefined,
          id: documentRequestUserId,
          perPage: 1000,
        }),
      ]);

      if (filesResponse.data) {
        const updatedFiles = filesResponse.data.items;
        setDocumentRequestUserFiles(current => [
          ...current.filter(file => !updatedFiles.some(updatedFile => updatedFile.id === file.id)),
          ...updatedFiles,
        ]);
      }

      if (userResponse.data) {
        setDocumentRequest(userResponse.data);
      }
    } finally {
      setIsUpdatingDocumentRequest(false);
    }
  }, [accessToken, getDocumentRequestUserFilesFn, documentRequestUserId, getDocumentRequestUserFn]);

  useEffect(() => {
    getDocumentRequestUserFn({ id: documentRequestUserId });
  }, [getDocumentRequestUserFn, documentRequestUserId]);

  useEffect(() => {
    if (creatorSlug) {
      getCreatorFn({ creatorSlug });
    }
  }, [getCreatorFn, creatorSlug]);

  useEffect(() => {
    if (documentRequest?.userWorkflowId) {
      getWorkflow({ userWorkflowId: documentRequest.userWorkflowId });
    }
  }, [documentRequest?.userWorkflowId, getWorkflow]);

  useEffect(() => {
    if ((accessToken || userId) && documentRequestUserId) {
      getDocumentRequestUserFilesFn({
        accessToken: accessToken || undefined,
        id: documentRequestUserId,
        perPage: 1000,
      });
    }
  }, [getDocumentRequestUserFilesFn, accessToken, userId, documentRequestUserId]);

  useEffect(() => {
    if (containerRef.current && !isTabsLayout) {
      setContainerOffset(containerRef.current.getBoundingClientRect().top);
    }
    if (filePreview && isTabsLayout) {
      setFilePreview(null);
    }
  }, [isTabsLayout, filePreview]);

  useEffect(() => {
    if (documentRequestUser?.compiledDocument?.id) {
      preloadDocument({
        accessToken,
        id: documentRequestUser.compiledDocument.id,
      })
        .then(blob => {
          const typeBlob = new Blob([blob], { type: documentRequestUser.compiledDocument?.mimeType });
          const blobPreviewUrl = window.URL.createObjectURL(typeBlob);
          setCompiledBlobUrl(blobPreviewUrl);
        })
        .catch(error => {
          logError(error);
        });
    }
  }, [documentRequestUser, accessToken]);

  useEffect(() => {
    if (documentRequestUserFilesLoaded) {
      setDocumentRequestUserFiles(documentRequestUserFilesLoaded.items);
      if (documentRequestUserFilesLoaded.items.filter(f => f.status !== DocumentRequestFileStatus.COMPLETE).length === 0) {
        setCurrentTab('uploaded');
      } else {
        setCurrentTab('pending');
      }
    }
  }, [documentRequestUserFilesLoaded]);

  useEffect(() => {
    if (documentRequestUser) {
      setDocumentRequest(documentRequestUser);
    }
  }, [documentRequestUser]);

  const { addListener, removeListener } = useEventsContext();

  useEffect(() => {
    const listener = addListener('docRequestUpdate', ({ id: emittedId }) => {
      if (emittedId === documentRequestUserId) {
        refetchFiles();
      }
    });

    return () => removeListener('docRequestUpdate', listener);
  }, [documentRequestUserId, addListener, removeListener, refetchFiles]);

  const reOpenDocumentRequest = async (): Promise<void> => {
    setIsUpdatingDocumentRequest(true);
    await reOpenDocumentRequestFn({ accessToken: accessToken || undefined, documentRequestUserId });
    await refetchFiles();
  };

  const handleDocumentUploaded: NonNullable<Parameters<typeof DocumentFileCard>[0]['onFinishUpload']> = async ({ documentRequestUserFileId }) => {
    if(!creatorId && (documentRequest?.status === DocumentRequestStatus.IN_REVIEW || documentRequest?.status === DocumentRequestStatus.SUCCEEDED)) {
      await reOpenDocumentRequest();
    }
    const response = await getDocumentRequestUserFile({
      fileId: documentRequestUserFileId,
    });
    if (response.data) {
      const newDocumentRequestUserFile: ThisDocumentRequestUserFile = response.data;
      setDocumentRequestUserFiles(current => [...current.filter(file => file.id !== newDocumentRequestUserFile.id), newDocumentRequestUserFile]);
    }
  };

  const handleDocumentsChanged: Parameters<typeof DocumentFileCard>[0]['onChangeDocuments'] = (documentRequestUserFileId, newDocuments) => {
    setDocumentRequestUserFiles(current => current.map(file => {
      if (file.id === documentRequestUserFileId) {
        return {
          ...file,
          documents: newDocuments,
        };
      }
      return file;
    }));
  };

  const completeDocumentRequest = async (): Promise<void> => {
    setIsUpdatingDocumentRequest(true);
    await completeDocumentRequestFn({ accessToken: accessToken || undefined, documentRequestUserId });
    await refetchFiles();
  };

  const toggleSubmitForReviewModal = async (skipRefetch : boolean):Promise<void> => {
    await updateDocumentRequestUserModal({
      id: documentRequestUserId,
      isCreator: !!creatorId,
      modalClose: true,
    });
    if(!skipRefetch) {
      refetchFiles();
    }
  };

  const submitDocumentRequest = async (): Promise<void> => {
    setIsUpdatingDocumentRequest(true);
    await updateDocumentRequestUser({
      id: documentRequestUserId,
      status: DocumentRequestStatus.IN_REVIEW,
      userWorkflowId: documentRequestUser?.userWorkflowId || null,
    });
    await toggleSubmitForReviewModal(true);
    await refetchFiles();
    setIsConfirmSubmitModalOpen(false);
  };

  useEffect(() => {
    if (documentRequest?.type === 'income-data' && documentRequest?.totalFiles === 0) {
      setAddDocumentModalOpen(true);
    }
  }, [documentRequest?.type, documentRequest?.totalFiles]);

  if (!documentRequest) {
    return (
      <>
        <Spacer size={80} />
        <ProgressIndicator isCentered isPage />
      </>
    );
  }

  const requiredFiles = documentRequestUserFiles.filter(f => f.isRequired);
  const uploadedRequiredFiles = requiredFiles.filter(f => f.status === DocumentRequestFileStatus.COMPLETE);
  const isComplete = documentRequest.status === DocumentRequestStatus.SUCCEEDED;
  const isInReview = documentRequest.status === DocumentRequestStatus.IN_REVIEW;
  const isCreator = creatorId === documentRequest?.creatorId;
  const modalStateFromDbIsClosed = isCreator
    ? documentRequest?.submitForReviewModalCloseForAttorney
    : documentRequest?.submitForReviewModalCloseForClient;
  const noTabs = documentRequestUserFiles.filter(f => f.status !== DocumentRequestFileStatus.COMPLETE).length === 0
    || documentRequestUserFiles.filter(f => f.status === DocumentRequestFileStatus.COMPLETE).length === 0;

  const outstandingFiles = documentRequestUserFiles
    .filter(f => f.status !== DocumentRequestFileStatus.COMPLETE)
    .sort((a, b) => (a.order || a.documentRequestFile?.order || 0) - (b.order || b.documentRequestFile?.order || 0))
    .sort((a, b) => (a.isRequired ? -1 : 1) - (b.isRequired ? -1 : 1));

  const uploadedFiles = documentRequestUserFiles
    .filter(f => f.status === DocumentRequestFileStatus.COMPLETE)
    .sort((a, b) => (a.order || a.documentRequestFile?.order || 0) - (b.order || b.documentRequestFile?.order || 0))
    .sort((a, b) => (a.isRequired ? -1 : 1) - (b.isRequired ? -1 : 1))
    .sort((a, b) => {
      const getMostRecentCreationDate = (documents: Array<{ createdAt: string }>): number =>
        Math.max(...documents.map(doc => new Date(doc.createdAt).getTime()));

      const aMostRecentDate = getMostRecentCreationDate(a.documents);
      const bMostRecentDate = getMostRecentCreationDate(b.documents);

      return bMostRecentDate - aMostRecentDate;
    });

  const downloadFiles = (overrideToken?: string ): void => {
    if (!accessToken && !overrideToken) {
      return;
    }
    if (uploadedFiles && documentRequest) {
      const allDocumentIds: string[] = [];
      uploadedFiles.forEach((file) => file.documents.forEach((document) => allDocumentIds.push(document.id)));
      const pdfDownloadLink = getUrlBase('/documents-proxy/documents/archive.pdf', {
        accessToken: accessToken || overrideToken,
        archiveName: documentRequest.documentRequest.title,
        documentId: allDocumentIds,
      });
      const a = window.document.createElement('a');
      a.href = pdfDownloadLink;
      a.style.display = 'none';
      a.download = `${documentRequest.documentRequest.title}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const areAllRequiredUploaded = uploadedFiles.length !== 0
    && requiredFiles.length === uploadedRequiredFiles.length
    && !isFetchingDocumentRequestUserFiles;

  const lastUpdated = documentRequestUserFiles
    .flatMap(file => file.documents)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]?.createdAt;

  return (
    <>
      {isAddDocumentModalOpen && (
        <>
          {documentRequest.type === 'income-data'
            ? <AddIncomeSourceModal
              onClose={() => setAddDocumentModalOpen(false)}
              documentRequestUserId={documentRequestUserId}
              refetchFiles={refetchFiles}
              refetchConsolidatedData={fetchConsolidatedData}
              isTableViewOpen={isTableViewOpen}
            />
            : <AddDocumentModal
              onClose={() => setAddDocumentModalOpen(false)}
              documentRequestUserId={documentRequestUserId}
              refetchFiles={refetchFiles} />
          }
        </>
      )}
      <Head>
        <title>{isInDashboard ? `${documentRequest.documentRequest.title} · Glade Dashboard` : documentRequest.documentRequest.title}</title>
      </Head>
      <DocumentAccessGate
        documentCreatorId={documentRequest.creatorId}
        documentPersonId={documentRequest.personId}
        isCollaborator={documentRequest.isCollaborator}
        isCenteredVertically
      >
        <MainPanelSubLayout
          homeTabTitle="Documents"
          header={{
            actions: [
              ...(isCreator && !isInDashboard && documentRequest.userWorkflowId
                ? [
                  {
                    icon: Workflow,
                    isSecondary: true,
                    label: 'View workflow',
                    onClick: () =>
                      router.push(getUrl(IDENTIFIERS.DASHBOARD_USER_WORKFLOW_DETAIL, { userWorkflowId: documentRequest.userWorkflowId as string })),
                  },
                ]
                : []),
              ...((documentRequest.documentRequest.isForCreator ? !isCreator || isInReview : isCreator)
              && !isComplete
              && areAllRequiredUploaded
              && isInReview
                ? [
                  {
                    icon: Check,
                    label: 'Mark document request as completed',
                    onClick: completeDocumentRequest,
                  },
                ]
                : []),
              ...((documentRequest.documentRequest.isForCreator ? !isCreator || isInReview : isCreator) && isComplete
                ? [
                  {
                    icon: Reopen,
                    label: 'Re-open document request',
                    onClick: reOpenDocumentRequest,
                  },
                ]
                : []),
              ...(!isComplete && (isCreator || documentRequest.type === 'income-data')
                ? [
                  {
                    icon: Plus,
                    isSecondary: true,
                    label: `Add ${documentRequest.type === 'income-data' ? 'Income Source' : 'an item'}`,
                    onClick: () => setAddDocumentModalOpen(true),
                  },
                ]
                : []),
              ...(uploadedFiles.length !== 0 && isCreator
                ? [
                  {
                    icon: FilePdf,
                    isSecondary: true,
                    label: 'Download PDF',
                    onClick: () => downloadFiles(),
                  },
                ]
                : []),
              ...(canShowTable && documentRequest.type === 'income-data'
                ? [{
                  icon: isTableViewOpen ? ToggleRight : ToggleLeft,
                  iconWeight: isTableViewOpen ? 'fill' as IconWeight : undefined,
                  isSecondary: true,
                  label: 'Table view',
                  onClick: () => setIsTableViewOpen(!isTableViewOpen),
                }]
                : []),
              ...(!isComplete && !isInReview && areAllRequiredUploaded
                ? [
                  {
                    icon: PaperPlaneRight,
                    label: 'Submit for review',
                    onClick: () => setIsConfirmSubmitModalOpen(true),
                  },
                ]
                : []),
            ],
            description: (
              <>
                {!areAllRequiredUploaded && !isCreator && !documentRequest.documentRequest.isForCreator && (
                  <p className="body-sm">You can only submit this document upload request for review after all required documents are uploaded.</p>
                )}
                {lastUpdated && (
                  <p className="body-sm" style={{ color: 'var(--color-gray-75)' }}>
                    Last activity: {format.datetimeLocalTZ.withoutTimezone({ datetime: lastUpdated })}
                  </p>
                )}
              </>
            ),
            title: documentRequest.documentRequest.title,
          }}
          pageContext={{
            isFetching: !workflowData,
            items: workflowData ? userWorkflowToContext(workflowData) : [],
          }}
          rightPanel={isTableViewOpen
            ? undefined
            :{
              component: (
                <>
                  {
                    <div className={s.rightPanel}>
                      <Header hierarchy="h2" title="Discussion" isSticky />
                      {workflowData && (
                        <UserWorkflowDiscussion
                          loadedCollaborators = {collaborators}
                          isLoaded={fetchingWorkflow?.isFetching === false}
                          userWorkflow={workflowData}
                          contextId={documentRequestUserId}
                          contextType="document-request"
                          isAsync
                        />
                      )}
                    </div>
                  }
                </>
              ),
              title: 'Discussion',
            }}
        >
          {isTableViewOpen
            ? <>
              <div style={{ display: 'flex', flex: 1, flexDirection: 'row' }}>
                <div className={s.table} style={filePreview ? { maxWidth: '50vw' } : {}}>
                  <Table>
                    <THead>
                      <TableRow>
                        {tableColumns.map((column, idx) => (
                          <TH
                            key={column}
                            columnId={column}
                            label={column}
                            className={idx === 0 ? s.firstColumn : undefined}
                            sort={
                              column === 'Pay Date'
                                ? {
                                  currentSortByField: 'Pay Date',
                                  currentSortDirection: payDateSortDirection,
                                  onSort: (_field, direction) => {
                                    setPayDateSortDirection(direction);
                                  },
                                }
                                : undefined
                            }
                          />
                        ))}
                      </TableRow>
                    </THead>
                    <TBody>
                      {consolidatedData?.rows
                        .map((row, rowIndex) => (
                          <>
                            <TableRow key={rowIndex}>
                              {tableColumns.map((colName, columnIndex) => (
                                <TD key={`${rowIndex}-${columnIndex}`}>
                                  <Buttons
                                    isWrapper
                                    onClick={() => columnIndex === 2
                                      ? setCollapsedRows(prev => prev.includes(row.id) ? prev.filter(r => r !== row.id) : [...prev, row.id])
                                      : null
                                    }
                                  >
                                    <div className={s.cell}>
                                      {columnIndex === 0 && (
                                        <Checkbox
                                          disabled={(!row.incomeSource || row.incomeSource==='Employment') && row.rows.length === 0}
                                          isChecked={selectedDocuments.some(d => row.rows.map(r => r.id).includes(d))}
                                          label=''
                                          size='sm'
                                          onChange={newValue => {
                                            if (newValue) {
                                              setSelectedDocuments(prev => [
                                                ...prev.filter(d => !row.rows.map(r => r.id).includes(d)),
                                                ...row.rows.map(r => r.id),
                                              ]);
                                            }
                                            else {
                                              setSelectedDocuments(prev => prev.filter(d => !row.rows.map(r => r.id).includes(d)));
                                            }
                                            if (!row.incomeSource || row.incomeSource==='Employment') {
                                              Bluebird.mapSeries(row.rows, async (document) => {
                                                await updateInternalDataFn({
                                                  data: {
                                                    ...fetchedData?.files
                                                      .find((f) => f.id === row.id)?.documents.find((d) => d.id === document.id)?.data,
                                                    useInCalculation: newValue,
                                                  },
                                                  documentId: document.id,
                                                });
                                              })
                                                .then(() => fetchConsolidatedData());
                                            } else {
                                              const existingMetaData = fetchedData?.files.find(f => f.id === row.id)?.metadata;
                                              if (!existingMetaData) return;
                                              let updatedMonthlyDollarValue;
                                              row.rows?.forEach(() => {
                                                updatedMonthlyDollarValue = [
                                                  ...existingMetaData.monthlyDollarValue?.map(mdv => ({ ...mdv, useInCalculation: newValue }),
                                                  ) ?? [],
                                                ];
                                              },
                                              );
                                              updateDocumentRequestUserFileFn({
                                                fileId: row.id,
                                                id: documentRequestUserId,
                                                metadata: { monthlyDollarValue: updatedMonthlyDollarValue },
                                              })
                                                .then(() => fetchConsolidatedData());
                                            }
                                          }}
                                        />

                                      )}

                                      {columnIndex === 1 && (
                                        <Checkbox
                                          disabled={(!row.incomeSource || row.incomeSource==='Employment') && row.rows.length === 0}
                                          isChecked={ selectedForMeanTest.some(d => row.rows.map(r => r.id).includes(d))
                                          }

                                          label=''
                                          size='sm'
                                          onChange={newValue => {
                                            if (newValue) {
                                              setSelectedForMeanTest(prev => [
                                                ...prev.filter(d => !row.rows.map(r => r.id).includes(d)),
                                                ...row.rows.map(r => r.id),
                                              ]);
                                            }
                                            else{
                                              setSelectedForMeanTest(prev => prev.filter(d => !row.rows.map(r => r.id).includes(d)));
                                            }
                                            if (!row.incomeSource || row.incomeSource==='Employment') {
                                              Bluebird.mapSeries(row.rows, async (document) => {
                                                await updateInternalDataFn({
                                                  data: {
                                                    ...fetchedData?.files
                                                      .find((f) => f.id === row.id)?.documents.find((d) => d.id === document.id)?.data,
                                                    useInMeansTest: newValue,
                                                  },
                                                  documentId: document.id,
                                                });
                                              })
                                                .then(() => fetchConsolidatedData());
                                            } else {
                                              const existingMetaData = fetchedData?.files.find(f => f.id === row.id)?.metadata;
                                              if (!existingMetaData) return;
                                              let updatedMonthlyDollarValue;
                                              row.rows?.forEach(() => {
                                                updatedMonthlyDollarValue = [
                                                  ...existingMetaData.monthlyDollarValue?.map(mdv => ({ ...mdv, useInMeansTest: newValue }),
                                                  ) ?? [],
                                                ];
                                              },
                                              );
                                              updateDocumentRequestUserFileFn({
                                                fileId: row.id,
                                                id: documentRequestUserId,
                                                metadata: { monthlyDollarValue: updatedMonthlyDollarValue },
                                              })
                                                .then(() => fetchConsolidatedData());
                                            }
                                          }}
                                        />

                                      )}

                                      { row.incomeSource && row.incomeSource !== 'Employment' && columnIndex === 2
                                         && <Buttons
                                           className={s.docButton}
                                           isWrapper
                                           onClick={(e) => {
                                             e.stopPropagation();
                                             setEditNonEmploymentIncomeModalOpen(row.id);
                                           }}
                                         > <PencilSimple/>
                                           {typeof row[colName as keyof FlatConsolidatedData] === 'number'
                                             ? formatAmountWithCurrency(row[colName as keyof FlatConsolidatedData] as number, Currency.Usd)
                                             : (row[colName as keyof FlatConsolidatedData] || '—')}
                                         </Buttons>
                                      }
                                      { columnIndex !== 0 && columnIndex !== 1 && (columnIndex !== 2 || !row.incomeSource || row.incomeSource==='Employment') && <span className='caption'>
                                        {typeof row[colName as keyof FlatConsolidatedData] === 'number'
                                          ? formatAmountWithCurrency(row[colName as keyof FlatConsolidatedData] as number, Currency.Usd)
                                          : (row[colName as keyof FlatConsolidatedData] || '—')}
                                      </span>

                                      }

                                      {columnIndex === 2 && row.rows.length > 0 && (
                                        <>
                                          {collapsedRows.includes(row.id) ? <CaretDown /> : <CaretUp />}
                                        </>
                                      )}
                                    </div>
                                  </Buttons>
                                </TD>
                              ))}
                            </TableRow>
                            {!collapsedRows.includes(row.id) && row.rows
                              .sort((rowA, rowB) => {
                                const dateA = rowA?.['Pay Date'] ? new Date(rowA['Pay Date']).getTime() : 0;
                                const dateB = rowB?.['Pay Date'] ? new Date(rowB['Pay Date']).getTime() : 0;
                                return payDateSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
                              })
                              .map((subRow, subIndex) => {
                                const rowData = fetchedData && fetchedData
                                  .files.find((f) => f.id === row.id)?.documents.find((d) => d.id === subRow.id)?.data;
                                return (
                                  <>
                                    <TableRow key={`${rowIndex}-${subIndex}`} className={subRow.id === filePreview?.documentId ? s.subRowSelected : s.subRow}>
                                      {tableColumns.map((colName, columnIndex) => (
                                        <TD key={`${rowIndex}-${columnIndex}`}>
                                          <div className={s.cell}>
                                            {
                                              columnIndex===0 && <>
                                                <Checkbox
                                                  isChecked={selectedDocuments.includes(subRow.id)}
                                                  label=''
                                                  size='sm'
                                                  onChange={newValue => {
                                                    if (rowData) {
                                                      setSelectedDocuments(prev => prev.includes(subRow.id)
                                                        ? prev.filter(r => r !== subRow.id)
                                                        : [...prev, subRow.id],
                                                      );
                                                      updateInternalDataFn({
                                                        data: {
                                                          ...rowData,
                                                          useInCalculation: newValue,
                                                        },
                                                        documentId: subRow.id,
                                                      })
                                                        .then(() => fetchConsolidatedData());
                                                    } else {
                                                      const existingMetaData = fetchedData?.files.find(f => f.id === row.id)?.metadata;
                                                      if (!existingMetaData) return;
                                                      let updatedMonthlyDollarValue;
                                                      row.rows?.forEach(() => {
                                                        updatedMonthlyDollarValue = [
                                                          ...existingMetaData.monthlyDollarValue?.map(mdv =>
                                                            mdv.month === subRow['Pay Date']
                                                              ? ({ ...mdv, useInCalculation: newValue })
                                                              : mdv,

                                                          ) ?? [],
                                                        ];
                                                      },
                                                      );
                                                      updateDocumentRequestUserFileFn({
                                                        fileId: row.id,
                                                        id: documentRequestUserId,
                                                        metadata: { monthlyDollarValue: updatedMonthlyDollarValue },
                                                      })
                                                        .then(() => fetchConsolidatedData());
                                                    }
                                                  }}
                                                />
                                              </>
                                            }

                                            {
                                              columnIndex===1 && <>
                                                {/* <div style={{ width: 16 }} /> */}
                                                <Checkbox
                                                  isChecked={selectedForMeanTest.includes(subRow.id)}
                                                  label=''
                                                  size='sm'
                                                  onChange={newValue => {
                                                    if (rowData) {
                                                      setSelectedForMeanTest(prev => prev.includes(subRow.id)
                                                        ? prev.filter(r => r !== subRow.id)
                                                        : [...prev, subRow.id],
                                                      );
                                                      updateInternalDataFn({
                                                        data: {
                                                          ...rowData,
                                                          useInMeansTest: newValue,
                                                        },
                                                        documentId: subRow.id,
                                                      })
                                                        .then(() => fetchConsolidatedData());
                                                    } else {
                                                      const existingMetaData = fetchedData?.files.find(f => f.id === row.id)?.metadata;
                                                      if (!existingMetaData) return;
                                                      let updatedMonthlyDollarValue;
                                                      row.rows?.forEach(() => {
                                                        updatedMonthlyDollarValue = [
                                                          ...existingMetaData.monthlyDollarValue?.map(mdv =>
                                                            mdv.month === subRow['Pay Date']
                                                              ? ({ ...mdv, useInMeansTest: newValue })
                                                              : mdv,

                                                          ) ?? [],
                                                        ];
                                                      },
                                                      );
                                                      updateDocumentRequestUserFileFn({
                                                        fileId: row.id,
                                                        id: documentRequestUserId,
                                                        metadata: { monthlyDollarValue: updatedMonthlyDollarValue },
                                                      })
                                                        .then(() => fetchConsolidatedData());
                                                    }
                                                  }}
                                                />
                                              </>
                                            }

                                            {(!row.incomeSource || row.incomeSource === 'Employment') && columnIndex === 2

                                                && <>
                                                  {/* <div style={{ width: 16 }} /> */}
                                                  <Tooltip text="Generated by Glade AI">
                                                    <NoodleAI size={16} />
                                                  </Tooltip>
                                                  <Buttons
                                                    className={s.docButton}
                                                    isWrapper
                                                    onClick={() => {
                                                      if (isTabsLayout) {
                                                        openDocumentRequestUserFilePane({ documentRequestUserFileId: row.id });
                                                        setDocumentId(subRow.id);
                                                      } else {
                                                        setFilePreview(filePreview && filePreview.documentId === subRow.id
                                                          ? null
                                                          : {
                                                            documentId: subRow.id,
                                                            documentRequestUserFileId: row.id,
                                                          });
                                                      }
                                                    }}
                                                  >
                                                    {subRow[colName as keyof FlatConsolidatedData]}
                                                  </Buttons>
                                                  <Button variant='secondary' size='xs' className={s.editButton} onClick={() => setPaystubModalOpen(subRow.id)}>Edit</Button>
                                                </>}

                                            {
                                              (row.incomeSource && row.incomeSource !== 'Employment') && columnIndex === 2
                                              && <>
                                                <span className='caption' onClick={() => alert('No document attached')}>
                                                  {(subRow[colName as keyof FlatConsolidatedData] || '—')}
                                                </span>
                                                <Button variant='secondary' size='xs' className={s.editButton} onClick={() => setEditNonEmploymentIncomeModalOpen(row.id)}>Edit</Button>
                                              </>
                                            }

                                            { columnIndex !== 0 && columnIndex !== 1 && (columnIndex !== 2)
                                                  && <span className='caption'>{typeof subRow[colName as keyof FlatConsolidatedData] === 'number'
                                                    ? formatAmountWithCurrency(subRow[colName as keyof FlatConsolidatedData] as number, Currency.Usd)
                                                    : (subRow[colName as keyof FlatConsolidatedData] || '—')}
                                                  </span>

                                            }
                                          </div>
                                        </TD>
                                      ))}
                                    </TableRow>
                                    {/* edit columns */}
                                    {rowData?.tableData && paystubModalOpen === subRow.id && <Modal drag hasForm title={`Edit paystub data`} align={filePreview ? "left" : "center"} onClose={() => setPaystubModalOpen(null)}>
                                      <form onSubmit={
                                        (e: React.FormEvent<HTMLFormElement>) => {
                                          e.preventDefault();
                                          const formData = new FormData(e.currentTarget as HTMLFormElement);
                                          updateInternalDataFn({
                                            data: {
                                              ...rowData,
                                              tableData: {
                                                ...Object.fromEntries(Array.from(formData.entries()).map(([key, value]) => [key, key === 'payDate' ? value : Number(value)])),
                                              },
                                            },
                                            documentId: subRow.id,
                                          })
                                            .then(async () => {
                                              await fetchConsolidatedData();
                                              setPaystubModalOpen(null);
                                            });
                                        }
                                      } className={s.paystubModal}>
                                        {Object.entries(keyToLabel)
                                          .sort(([, labelA], [, labelB]) => {
                                            const indexA = tableColumns.findIndex(col => col === labelA);
                                            const indexB = tableColumns.findIndex(col => col === labelB);
                                            return indexA - indexB;
                                          })
                                          .map(([key, label]) => (
                                            <Label key={key} label={key === 'payDate' ? label : `${label} (USD)`} compact>
                                              <input
                                                type={key === 'payDate' ? 'date' : 'string'}
                                                name={key}
                                                id={key}
                                                onClick={(e) => e.currentTarget.select()}
                                                defaultValue={(rowData?.tableData as Record<string, string | undefined>)[key] || ''}
                                              />
                                            </Label>
                                          ))
                                        }
                                        <Button variant="primary" type="submit" size='md'>
                                          Submit
                                        </Button>
                                      </form>
                                    </Modal>}
                                  </>
                                );
                              })}
                            {/* Edit non employment type rows */}
                            {
                              editNonEmploymentIncomeModalOpen === row.id
                                ? <EditNonEmploymentIncomeSourceModal
                                  onClose={() => setEditNonEmploymentIncomeModalOpen(null)}
                                  documentRequestUserId={documentRequestUserId}
                                  refetchFiles={fetchConsolidatedData}
                                  row={row}
                                  fetchedData={fetchedData}
                                />
                                : null
                            }
                          </>
                        ))}
                    </TBody>
                    <TFoot>
                      <TableRow>
                        {tableColumns.map((colName, columnIndex) => (
                          <TD key={`totals-${columnIndex}`}>
                            <span className={colName === 'Include in Monthly Totals' ? 'caption-bold' : 'caption'}>
                              {typeof consolidatedData?.total[colName as keyof FlatConsolidatedData] === 'number'
                                ? formatAmountWithCurrency(consolidatedData?.total[colName as keyof FlatConsolidatedData] as number, Currency.Usd)
                                : (consolidatedData?.total[colName as keyof FlatConsolidatedData] || '—')}
                            </span>
                          </TD>
                        ))}
                      </TableRow>
                    </TFoot>
                  </Table>
                  <MeansTestSection
                    meansTestData={meansTestData}
                    tableData={fetchedData}
                    selectedForMeanTest={selectedForMeanTest}
                    userWorkflowId={documentRequest?.userWorkflowId}
                    documentRequestUserId={documentRequestUserId}
                    fetchingMeansTestData={fetchingMeansTestData}
                  />
                </div>
                {filePreview && <PayOrganizerFilePreview
                  documentRequestUserFileId={filePreview.documentRequestUserFileId}
                  documentId={filePreview.documentId}
                  onClose={() => setFilePreview(null)}
                />}
              </div>
              {valueModalOpen && <Modal onClose={() => setValueModalOpen(false)} hasPadding title="Update value">
                <Input
                  placeholder="Enter value"
                  compact
                  label='Value'
                />
                <Spacer />
                <Button fullWidth size="md" variant='primary' onClick={() => setValueModalOpen(false)}>
                  Save
                </Button>
              </Modal>}
            </>
            : <div className={s.wrapper} ref={containerRef}>
              {isCreator
                ? (
                  <>
                    {(isComplete || isInReview || (areAllRequiredUploaded && requiredFiles.length > 0)) && (
                      <div className={s.banner}>
                        <BannerStatus
                          status={isComplete ? 'success' : 'warning'}
                          label={
                            (isComplete && 'Document request complete.')
                          || (isInReview
                            && !documentRequest.documentRequest.isForCreator
                            && 'Contact has uploaded all required documents and has submitted this request for your review.')
                          || (isInReview
                            && documentRequest.documentRequest.isForCreator
                            && `You have uploaded all required documents and this request has been submitted.`)
                          || (documentRequest.documentRequest.isForCreator
                            && `All required documents have been uploaded, but you have not yet submitted this request.`)
                          || 'Contact has uploaded all required documents, but has not yet submitted this request for review. They may still be uploading optional documents.'
                          }
                          cta={!isComplete && !isInReview && !documentRequest.documentRequest.isForCreator
                            ?{label:"Mark as completed anyway", onClick:completeDocumentRequest}
                            : undefined}
                        />
                      </div>
                    )}
                  </>
                )
                : (
                  <>
                    {(isComplete || isInReview || areAllRequiredUploaded) && !isFetchingDocumentRequestUserFiles && (
                      <div className={s.banner}>
                        <BannerStatus
                          status={isComplete ? 'success' : 'warning'}
                          label={
                            (isComplete && 'Document request complete.')
                          || (isInReview
                            && documentRequest.documentRequest.isForCreator
                            && `${creator?.name || 'The creator'} has uploaded all required documents and has submitted this request for your review.`)
                          || (isInReview && 'You have submitted all the required documents. Your documents are now under review by the provider.')
                          || (documentRequest.documentRequest.isForCreator
                            && `${
                              creator?.name || 'The creator'
                            } has uploaded all required documents but has not yet submitted this request for review. They may still be uploading optional documents.`)
                          || 'You have uploaded all required documents. If you are finished uploading documents, please submit.'
                          }
                          cta={!isComplete && !isInReview && documentRequest.documentRequest.isForCreator
                            ?{label:"Mark as completed anyway", onClick:completeDocumentRequest}
                            : undefined}
                        />
                      </div>
                    )}
                  </>
                )}
              {isTabsLayout
                ? (
                  <div className={s.container}>
                    {documentRequest.compiledDocument?.id && compiledBlobUrl && (
                      <>
                        <a href={compiledBlobUrl} className={s.download} download={documentRequest.compiledDocument.fileName}>
                          <FileArrowDown weight="fill" size={16} />
                          Download compiled documents
                        </a>
                        <Spacer size="16px" />
                      </>
                    )}
                    {!isCreator && !isComplete && !isInReview && !documentRequest.documentRequest.isForCreator && (
                      <>
                        {!isMobile && <Spacer size="16px" />}
                        <Buttons
                          isSecondary
                          isFullWidth
                          onClick={() => setIsConfirmSubmitModalOpen(true)}
                          isFetching={isUpdatingDocumentRequest}
                          disabled={!areAllRequiredUploaded}
                        >
                          Submit for review
                        </Buttons>
                        {!isMobile && <Spacer size="16px" />}
                        {!areAllRequiredUploaded && (
                          <div className={s.reviewInfo}>
                            <p>You can only submit this document upload request for review after all required documents are uploaded.</p>
                          </div>
                        )}
                        {isMobile && noTabs && <Spacer size={16} />}
                      </>
                    )}
                    <div className={s.files}>
                      {!noTabs && (
                        <Tabs
                          inputName="status"
                          inputs={[
                            {
                              id: 'pending',
                              label: `Pending (${documentRequestUserFiles.filter(f => f.status !== DocumentRequestFileStatus.COMPLETE).length})`,
                            },
                            {
                              id: 'uploaded',
                              label: `Uploaded (${documentRequestUserFiles.filter(f => f.status === DocumentRequestFileStatus.COMPLETE).length})`,
                            },
                          ]}
                          checked={currentTab}
                          onChange={newTab => setCurrentTab(newTab)}
                        />
                      )}
                      {isFetchingDocumentRequestUserFiles && <ProgressIndicator isCentered isPage />}
                      {(currentTab === 'pending' ? outstandingFiles : uploadedFiles).map(file => (
                        <DocumentFileCard
                          canDelete={isCreator && !isComplete}
                          canUpdateIsRequired={isCreator}
                          key={file.id}
                          creatorSlug={creatorSlug}
                          creatorName={creator?.name}
                          documentRequestUserId={documentRequestUserId}
                          documentRequestUserFileId={file.id}
                          fileName={file.fileName || file.documentRequestFile?.fileName || ''}
                          fileDescription={file.fileDescription || file.documentRequestFile?.fileDescription || ''}
                          onFinishUpload={handleDocumentUploaded}
                          isRequired={file.isRequired}
                          hasPermission={file.hasPermission}
                          isForYourEyesOnly={
                            file.isForYourEyesOnly !== null ? file.isForYourEyesOnly : file.documentRequestFile?.isForYourEyesOnly || false
                          }
                          refetchFiles={refetchFiles}
                          documents={file.documents}
                          onChangeDocuments={handleDocumentsChanged}
                          actionRequired={file.rejectionMessage
                            ? {
                              rejectedAt: file.rejectedAt,
                              rejectionMessage: file.rejectionMessage,
                            }
                            : undefined}
                          showDocumentValidationStatus={currentTab !== 'pending'}
                        />
                      ))}
                      {isComplete && !isCreator && (
                        <CustomLink
                          to={getUrl(IDENTIFIERS.USER_PROFILE, {
                            creatorSlug,
                          })}
                          className={s.successCTA}
                        >
                          <small>This document request is complete</small>
                          <p>View your chat</p>
                        </CustomLink>
                      )}
                    </div>
                  </div>
                )
                : <div className={s.containerColumns}>
                  {outstandingFiles.length > 0 && (
                    <div style={{ height: `calc(min(100vh, 100dvh) - ${containerOffset}px)` }}>
                      <Header hierarchy="h2" title={`${documentRequest.type === 'income-data' ? 'Income Sources' : 'Outstanding'} (${outstandingFiles.length})`} isSticky />
                      <div className={s.containerColumnsList}>
                        {outstandingFiles.map(file => (
                          <DocumentFileCard
                            canDelete={isCreator && !isComplete}
                            canUpdateIsRequired={isCreator}
                            key={file.id}
                            creatorSlug={creatorSlug}
                            creatorName={creator?.name}
                            documentRequestUserId={documentRequestUserId}
                            documentRequestUserFileId={file.id}
                            fileName={file.fileName || file.documentRequestFile?.fileName || ''}
                            fileDescription={file.fileDescription || file.documentRequestFile?.fileDescription || ''}
                            onFinishUpload={handleDocumentUploaded}
                            isRequired={file.isRequired}
                            hasPermission={file.hasPermission}
                            isIncomeSource={documentRequest.type === 'income-data'}
                            isForYourEyesOnly={
                              file.isForYourEyesOnly !== null ? file.isForYourEyesOnly : file.documentRequestFile?.isForYourEyesOnly || false
                            }
                            refetchFiles={refetchFiles}
                            documents={file.documents}
                            onChangeDocuments={handleDocumentsChanged}
                            actionRequired={file.rejectionMessage
                              ? {
                                rejectedAt: file.rejectedAt,
                                rejectionMessage: file.rejectionMessage,
                              }
                              : undefined}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ height: `calc(min(100vh, 100dvh) - ${containerOffset}px)` }}>
                    <Header hierarchy="h2" title={isFetchingDocumentRequestUserFiles ? 'Loading...' : `Uploaded (${uploadedFiles.length})`} isSticky />
                    <div className={s.containerColumnsList}>
                      {uploadedFiles.map(file => (
                        <DocumentFileCard
                          canDelete={isCreator && !isComplete}
                          canUpdateIsRequired={isCreator}
                          key={file.id}
                          creatorSlug={creatorSlug}
                          creatorName={creator?.name}
                          documentRequestUserId={documentRequestUserId}
                          documentRequestUserFileId={file.id}
                          fileName={file.fileName || file.documentRequestFile?.fileName || ''}
                          fileDescription={file.fileDescription || file.documentRequestFile?.fileDescription || ''}
                          onFinishUpload={handleDocumentUploaded}
                          isRequired={file.isRequired}
                          isIncomeSource={documentRequest.type === 'income-data'}
                          documents={file.documents}
                          onChangeDocuments={handleDocumentsChanged}
                          hasPermission={file.hasPermission}
                          isForYourEyesOnly={
                            file.isForYourEyesOnly !== null ? file.isForYourEyesOnly : file.documentRequestFile?.isForYourEyesOnly || false
                          }
                          refetchFiles={refetchFiles}
                          actionRequired={file.rejectionMessage
                            ? {
                              rejectedAt: file.rejectedAt,
                              rejectionMessage: file.rejectionMessage,
                            }
                            : undefined}
                          showDocumentValidationStatus
                        />
                      ))}
                    </div>
                  </div>
                </div>}

              {isConfirmSubmitModalOpen && (
                <Modal title="Confirm submission" onClose={() => setIsConfirmSubmitModalOpen(false)}>
                  <div className={s.confirmSubmit}>
                    {creator
                      ? (
                        <>
                          <UserImage size={80}
                            url={creator?.logoCompact?.url || creator?.person?.image} color={creator.primaryColour?.hex} name={creator.name} />
                          <h2 className="body-md-bold">
                          Your documents will be submitted{documentRequest.documentRequest.isForCreator ? '' : ` to ${creator?.name} for review`}.
                          </h2>
                          {!documentRequest.documentRequest.isForCreator && (
                            <p className="body-sm">
                            You will not be able to change any documents while your documents are in review. {creator.name} will notify you if any
                            documents need to be re-uploaded.
                            </p>
                          )}
                          <Buttons onClick={submitDocumentRequest} isSecondary isFullWidth isFetching={isUpdatingDocumentRequest}>
                          Submit
                          </Buttons>
                        </>
                      )
                      : (
                        <ProgressIndicator isCentered />
                      )}
                  </div>
                </Modal>
              )}
              {!modalStateFromDbIsClosed && !isComplete && !isInReview && areAllRequiredUploaded && documentRequest.type !== 'income-data' && (
                <Modal
                  hasPadding
                  onClose={() => {
                    toggleSubmitForReviewModal(false);
                  }}
                  closeIcon={<XCircle weight="fill" size={24} color="var(--color-gray-100)" />}
                  title=""
                >
                  <div className={s.modalContent}>
                    <div className={s.modalHeader}>
                      <WarningCircle size={96} weight="fill" color="var(--color-warning)" />
                      <h2 className={s.modalHeaderTitle}>Do you want to proceed with submission?</h2>
                      <p className={[s.modalBodyText, s.modalTextWarning].join(' ')}>
                        {isCreator ? 'Contact has': 'You have'} uploaded all required documents, but have not yet submitted this request for review. If {isCreator ? 'they': 'you'} are finished uploading documents, please submit the request now. Otherwise, close this window to continue uploading documents.
                      </p>
                    </div>
                    <div className={s.modalBodyButtons}>
                      <Button size='lg' className={s.modalBodyButton} variant='primary' onClick={submitDocumentRequest}>Submit</Button>
                      <Button size='lg' className={s.modalBodyButton} variant='secondary' onClick={() => toggleSubmitForReviewModal(false)}>Continue Uploading</Button>
                    </div>
                  </div>
                </Modal>
              )}
            </div>}
        </MainPanelSubLayout>
      </DocumentAccessGate>
    </>
  );
};

export default DocumentRequestUserDetail;
